import React, { useState, useMemo } from 'react';
import { Check, ChevronDown } from 'lucide-react';

const CLUBS = ['Driver', '3W', '5W', '3H', '4H', '3i', '4i', '5i', '6i', '7i', '8i', '9i', 'PW', 'GW', 'SW', 'LW'];

export const ShotTracker = ({ onLogShot, onCancel, recommendation, distToGreen }) => {
  const [selectedClub, setSelectedClub] = useState(recommendation?.recommendation || '');
  const [expanded, setExpanded] = useState(false);
  const otherClubs = useMemo(() => {
    const suggested = (recommendation?.alternatives || []).map(a => (typeof a === 'string' ? a : a.club));
    return CLUBS.filter(c => !suggested.includes(c));
  }, [recommendation]);

  const handleConfirm = () => {
    if (!selectedClub) return;
    onLogShot(selectedClub);
  };

  return (
    <div className="absolute bottom-28 left-3 right-3 z-20" data-testid="shot-tracker-panel">
      <div className="bg-black/90 backdrop-blur-xl rounded-2xl border border-white/15 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-4 pt-3 pb-2 border-b border-white/10">
          <div className="flex items-center justify-between">
            <span className="text-white text-sm font-bold">Log Shot</span>
            {distToGreen && (
              <span className="text-white/50 text-xs">{distToGreen} yds to green</span>
            )}
          </div>
          {/* Recommendation badge */}
          {recommendation && (
            <div className="mt-1.5 flex items-center gap-2" data-testid="club-recommendation-badge">
              <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg px-2.5 py-1 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-emerald-300 text-xs font-bold">{recommendation.recommendation}</span>
                <span className="text-emerald-300/60 text-[10px]">~{recommendation.avg_distance} yds</span>
              </div>
              <span className="text-white/30 text-[10px]">
                {recommendation.confidence === 'personal' ? 'Your avg' : 'Typical'}
              </span>
            </div>
          )}
        </div>

        {/* Quick select: top clubs */}
        <div className="px-3 py-2.5">
          <div className="flex flex-wrap gap-1.5">
            {(recommendation?.alternatives || CLUBS.slice(0, 6)).map((alt) => {
              const club = typeof alt === 'string' ? alt : alt.club;
              const avg = typeof alt === 'object' ? alt.avg : null;
              const isSelected = selectedClub === club;
              return (
                <button
                  key={club}
                  onClick={() => setSelectedClub(club)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                    isSelected
                      ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                      : 'bg-white/10 text-white/70 hover:bg-white/15'
                  }`}
                  data-testid={`shot-club-${club}`}
                >
                  {club}
                  {avg && <span className="text-[9px] ml-1 opacity-60">{avg}</span>}
                </button>
              );
            })}
          </div>

          {/* Expand full list */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full mt-2 flex items-center justify-center gap-1 text-white/40 text-[10px] hover:text-white/60 transition-colors"
            data-testid="expand-club-list"
          >
            {expanded ? 'Less' : 'All clubs'}
            <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>

          {expanded && (
            <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-white/10">
              {otherClubs.map(club => (
                <button
                  key={club}
                  onClick={() => setSelectedClub(club)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                    selectedClub === club
                      ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                      : 'bg-white/10 text-white/70 hover:bg-white/15'
                  }`}
                  data-testid={`shot-club-${club}`}
                >
                  {club}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 px-3 pb-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl bg-white/10 text-white/60 text-sm font-semibold hover:bg-white/15 transition-colors"
            data-testid="shot-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedClub}
            className="flex-1 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-bold disabled:opacity-30 hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1.5"
            data-testid="shot-confirm"
          >
            <Check className="w-4 h-4" />
            {selectedClub || 'Select club'}
          </button>
        </div>
      </div>
    </div>
  );
};
